/**
 * ImageMotion motion tiers / fal model catalogue types.
 * Admin fields (endpoints, costs, notes) must never ship on customer paths.
 */

export type AdapterType =
  | 'kling_start_image'
  | 'kling_image_url'
  | 'kling_v2'
  | 'seedance'
  | 'hailuo'
  | 'veo_first_last'
  | 'generic';

export type AspectRatioBehaviour = 'auto' | 'lock_16_9' | 'match_source' | string;

export type MotionTierKey = 'standard_motion' | 'premium_motion' | 'ultra_motion' | string;

export type TierVersionStatus = 'draft' | 'active' | 'archived';

export type ModelTestStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'partial'
  | string;

export type ModelTestResultStatus =
  | 'pending'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | string;

/** Customer-safe tier fields only (no endpoints, costs, or admin notes). */
export interface PublicMotionTier {
  tier_id: string;
  tier_key: MotionTierKey;
  display_order: number;
  version_id: string;
  display_name: string;
  description: string;
  badge_text: string | null;
  credit_cost: number;
  max_images: number;
  duration_seconds: number;
  resolution: string | null;
  is_recommended: boolean;
  aspect_ratio_behaviour: AspectRatioBehaviour;
}

export interface AdminMotionModel {
  id: string;
  provider: string;
  endpoint: string;
  admin_name: string;
  adapter_type: AdapterType;
  supported_durations: number[];
  supported_resolutions: string[];
  supported_aspect_ratios: string[];
  supports_audio: boolean;
  supports_end_frame: boolean;
  estimated_cost_fixed_usd: number | null;
  estimated_cost_formula: string | null;
  default_settings: Record<string, unknown>;
  input_schema: Record<string, unknown>;
  is_active: boolean;
  testing_only: boolean;
  admin_notes: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AdminTierVersionFields {
  version_id: string;
  version: number;
  status: TierVersionStatus;
  display_name: string;
  description: string;
  badge_text: string | null;
  model_id: string | null;
  model_admin_name: string | null;
  model_endpoint: string | null;
  fallback_model_id: string | null;
  fallback_model_admin_name: string | null;
  fallback_model_endpoint: string | null;
  fallback_enabled: boolean;
  fallback_on_failures: string[];
  fallback_max_attempts: number;
  credit_cost: number;
  max_images: number;
  duration_seconds: number;
  resolution: string | null;
  aspect_ratio_behaviour: AspectRatioBehaviour;
  generate_audio: boolean;
  is_enabled: boolean;
  is_recommended: boolean;
  default_prompt: string | null;
  negative_prompt: string | null;
  model_settings: Record<string, unknown>;
  estimated_fal_cost_usd: number | null;
  admin_notes: string | null;
  published_at: string | null;
}

export interface AdminTierProfitability {
  revenue_typical_usd: number;
  revenue_conservative_usd: number;
  profit_typical_usd: number;
  profit_conservative_usd: number;
  margin_typical: number | null;
  margin_conservative: number | null;
  cost_per_100_usd: number;
  cost_per_1000_usd: number;
}

export type AdminTierActiveVersion = AdminTierVersionFields & AdminTierProfitability;

/** Draft version row (may lack joined model names / profitability). */
export interface AdminTierDraftVersion {
  id: string;
  tier_id: string;
  version: number;
  status: TierVersionStatus;
  display_name: string;
  description: string;
  badge_text: string | null;
  model_id: string | null;
  fallback_model_id: string | null;
  fallback_enabled: boolean;
  fallback_on_failures: string[] | null;
  fallback_max_attempts: number;
  credit_cost: number;
  max_images: number;
  duration_seconds: number;
  resolution: string | null;
  aspect_ratio_behaviour: AspectRatioBehaviour;
  generate_audio: boolean;
  is_enabled: boolean;
  is_recommended: boolean;
  default_prompt: string | null;
  negative_prompt: string | null;
  model_settings: Record<string, unknown>;
  estimated_fal_cost_usd: number | null;
  admin_notes: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AdminMotionTier {
  id: string;
  tier_key: MotionTierKey;
  display_order: number;
  created_at: string;
  updated_at: string;
  active_version: AdminTierActiveVersion | null;
  draft_versions: AdminTierDraftVersion[] | null;
}

export interface AdminListTiersResponse {
  success: boolean;
  error?: string;
  credit_value_usd_typical?: number;
  credit_value_usd_conservative?: number;
  tiers?: AdminMotionTier[];
}

export interface AdminListModelsResponse {
  success: boolean;
  error?: string;
  models?: AdminMotionModel[];
}

export interface UpsertMotionModelPayload {
  id?: string;
  provider?: string;
  endpoint: string;
  admin_name: string;
  adapter_type?: AdapterType;
  supported_durations?: number[];
  supported_resolutions?: string[];
  supported_aspect_ratios?: string[];
  supports_audio?: boolean;
  supports_end_frame?: boolean;
  estimated_cost_fixed_usd?: number | null;
  estimated_cost_formula?: string | null;
  default_settings?: Record<string, unknown>;
  input_schema?: Record<string, unknown>;
  is_active?: boolean;
  testing_only?: boolean;
  admin_notes?: string | null;
}

export interface SaveTierDraftPayload {
  display_name?: string;
  description?: string;
  badge_text?: string | null;
  model_id?: string | null;
  fallback_model_id?: string | null;
  fallback_enabled?: boolean;
  fallback_on_failures?: string[];
  fallback_max_attempts?: number;
  credit_cost?: number;
  max_images?: number;
  duration_seconds?: number;
  resolution?: string | null;
  aspect_ratio_behaviour?: AspectRatioBehaviour;
  generate_audio?: boolean;
  is_enabled?: boolean;
  is_recommended?: boolean;
  default_prompt?: string | null;
  negative_prompt?: string | null;
  model_settings?: Record<string, unknown>;
  estimated_fal_cost_usd?: number | null;
  admin_notes?: string | null;
}

export interface AdminModelTestScores {
  score_image_preservation?: number | null;
  score_architectural_stability?: number | null;
  score_realism?: number | null;
  score_camera_movement?: number | null;
  score_prompt_accuracy?: number | null;
  score_commercial_usability?: number | null;
}

export interface AdminModelTestResult extends AdminModelTestScores {
  id: string;
  test_id: string;
  model_id: string;
  generation_id: string | null;
  fal_endpoint: string | null;
  video_url: string | null;
  estimated_cost_usd: number | null;
  actual_cost_usd: number | null;
  processing_time_ms: number | null;
  status: ModelTestResultStatus;
  error_message: string | null;
  rating_notes: string | null;
  created_at?: string;
  model_admin_name?: string | null;
}

export interface AdminModelTest {
  id: string;
  admin_user_id: string;
  image_url: string;
  image_path: string | null;
  prompt: string;
  duration_seconds: number | null;
  resolution: string | null;
  aspect_ratio: string | null;
  estimated_total_cost_usd: number | null;
  status: ModelTestStatus;
  created_at: string;
  completed_at: string | null;
  results?: AdminModelTestResult[];
}

export interface MotionAnalyticsSummary {
  total_generations: number;
  completed: number;
  failed: number;
  fallback_attempts: number;
  success_rate: number | null;
  failure_rate: number | null;
  fallback_rate: number | null;
  avg_processing_time_ms: number | null;
  estimated_spend_usd: number;
  credits_charged: number;
  estimated_revenue_usd: number;
  estimated_margin: number | null;
  regeneration_rate: number | null;
}

export interface MotionAnalyticsByTier {
  tier_id: string | null;
  tier_key: string | null;
  tier_name: string | null;
  generations: number;
  completed: number;
  failed: number;
  estimated_spend_usd: number;
  credits_charged: number;
}

export interface MotionAnalyticsByModel {
  model_id: string | null;
  fal_endpoint: string | null;
  model_admin_name: string | null;
  generations: number;
  completed: number;
  failed: number;
  fallback_attempts: number;
  avg_processing_time_ms: number | null;
  estimated_spend_usd: number;
}

export interface MotionAnalyticsResponse {
  success: boolean;
  error?: string;
  from?: string;
  to?: string;
  credit_value_usd_typical?: number;
  summary?: MotionAnalyticsSummary;
  by_tier?: MotionAnalyticsByTier[];
  by_model?: MotionAnalyticsByModel[];
}

export interface RpcResult {
  success: boolean;
  error?: string;
  [key: string]: unknown;
}

export const ADAPTER_TYPE_OPTIONS: { value: AdapterType; label: string }[] = [
  { value: 'kling_v2', label: 'Kling v2' },
  { value: 'kling_start_image', label: 'Kling start_image' },
  { value: 'kling_image_url', label: 'Kling image_url' },
  { value: 'seedance', label: 'Seedance' },
  { value: 'hailuo', label: 'Hailuo' },
  { value: 'veo_first_last', label: 'Veo first/last frame' },
  { value: 'generic', label: 'Generic' },
];

export const ASPECT_RATIO_BEHAVIOUR_OPTIONS: { value: AspectRatioBehaviour; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'lock_16_9', label: 'Lock 16:9' },
  { value: 'match_source', label: 'Match source' },
];

export const TIER_ASSIGN_OPTIONS: { key: MotionTierKey; label: string }[] = [
  { key: 'standard_motion', label: 'Standard' },
  { key: 'premium_motion', label: 'Premium' },
  { key: 'ultra_motion', label: 'Ultra' },
];
